import Link from 'next/link';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { type DropTargetMonitor, useDrop } from 'react-dnd';

import { useTrackSidebarInteraction } from '@air/analytics';
import { type LibraryWithPermissions } from '@air/api/types';
import { TreeItemAction } from '@air/component-tree';
import { ChevronDownIcon, EllipsisIcon, PlusIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { DropdownMenu, type DropdownMenuProps } from '@air/primitive-dropdown-menu';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { useAirModal } from '@air/provider-modal';

import { useLibraryRootBoards } from '~/components/LibraryBeta/hooks/queries/useLibraryRootBoards';
import { useLibraryMenuOptions } from '~/components/LibraryBeta/hooks/useLibraryMenuOptions';
import { LibraryNaigationDropdownMenuContent } from '~/components/LibraryBeta/LibraryNavigation/components/LibraryNavigationDropdownMenuContent';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { canMoveBoardToLibrary } from '~/components/Shared/Drag/canMoveBoardToLibrary';
import {
  boardDragTypes,
  DragType,
  type DropData,
  type GetBoardDragItem,
  isBoardDragType,
} from '~/components/Shared/Drag/dragTypes';
import { DNDLibraryDrag } from '~/components/v2/components/shared/dnd/DNDLibraryDrag';
import { WorkspaceSidebarBoardsList } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarBoardsList';
import { WorkspaceSidebarCollapsible } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCollapsible';
import { WorkspaceSidebarLibrariesListItemContextMenu } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesListItemContextMenu';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { Pathname } from '~/constants/pathname';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useLibraryBoardRearrange } from '~/hooks/useLibraryBoardRearrange';
import { useLibraryPermissionsCache } from '~/hooks/useLibraryPermissionsCache';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { isCentralizedBoardInCurrentLibrarySelector } from '~/store/centralizedBoard/selectors';
import { selectedBoardsSelector } from '~/store/selectedItems/selectors';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
import { useAirSelector, useAirStore } from '~/utils/ReduxUtils';

const SIDEBAR_LIBRARY_CONTEXT_MENU_LOCK_TOKEN_PREFIX = 'sidebar-library-context-menu';

export type WorkspaceSidebarLibrariesListItemProps = {
  library: LibraryWithPermissions;
  draggable?: boolean;
  index?: number;
};

export const WorkspaceSidebarLibrariesListItem = memo<WorkspaceSidebarLibrariesListItemProps>(
  ({ library, draggable = false, index = 0 }) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const open = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: library.id })?.open);
    const touched = useWorkspaceSidebarSectionStore((store) => !!store.getTreeItem({ id: library.id })?.touched);
    const setTreeItem = useWorkspaceSidebarSectionStore((state) => state.setTreeItem);
    const toggleTreeItemOpen = useWorkspaceSidebarSectionStore((state) => state.toggleTreeItemOpen);
    const { currentWorkspace } = useCurrentWorkspace();
    const currentLibraryId = useURLLibraryIdSelector();
    const { trackSidebarInteraction } = useTrackSidebarInteraction();

    const isLibraryActive = currentLibraryId === library.id;

    const onToggleOpen = useCallback(() => {
      toggleTreeItemOpen({ id: library.id });
      trackSidebarInteraction({
        action: open ? 'collapse' : 'expand',
        type: 'library',
      });
    }, [toggleTreeItemOpen, library.id, open, trackSidebarInteraction]);

    const {
      data: libraryRootBoards,
      hasMore,
      isInitialLoading: isLoadingBoards,
      isLoadingMore,
      loadNextPage,
    } = useLibraryRootBoards({
      workspaceId: currentWorkspace?.id,
      libraryId: library.id,
      enabled: open,
    });
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
    const { menuOptions } = useLibraryMenuOptions({
      initialData: library,
      libraryId: library.id,
    });
    const [showCreateBoardModal] = useAirModal(CreateBoardModal);
    const store = useAirStore();
    const selectedBoards = selectedBoardsSelector(store.getState());
    const { getLibraryPermissions } = useLibraryPermissionsCache();

    const isCentralizedBoardInCurrentLibrary = useAirSelector((state) =>
      isCentralizedBoardInCurrentLibrarySelector(state, library.id),
    );

    useFetchObjectsPermissions({
      objects: {
        boardIds: libraryRootBoards.map((board) => board.id),
      },
    });

    useEffect(() => {
      if (touched) {
        return;
      }

      if (isCentralizedBoardInCurrentLibrary) {
        setTreeItem({ id: library.id, open: true, touched: false });
      }
    }, [isCentralizedBoardInCurrentLibrary, library.id, setTreeItem, touched]);

    const checkIfCanDrop = useCallback(
      (monitor: DropTargetMonitor<GetBoardDragItem>) => {
        const item = monitor.getItem();

        if (!item) {
          return false;
        }

        const libraryPermissions = getLibraryPermissions(library.id);

        if (!canCreateBoard(libraryPermissions)) {
          return false;
        }

        if (isBoardDragType(item.type) || selectedBoards.length) {
          return canMoveBoardToLibrary(item.firstItem, library.id);
        }

        return false;
      },
      [getLibraryPermissions, library.id, selectedBoards.length],
    );

    const [{ isOver, canDrop }, drop] = useDrop({
      accept: boardDragTypes,
      drop: (): DropData => ({
        type: 'move-to-library',
        location: 'library',
        data: library,
      }),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
      canDrop: (_props, monitor) => {
        const itemType = monitor.getItemType();

        // Libraries can't be dropped on other libraries
        if (itemType === DragType.library) {
          return false;
        }

        return checkIfCanDrop(monitor);
      },
    });

    const onLibraryClick = useCallback(() => {
      if (touched || open) {
        trackSidebarInteraction({
          action: 'link_click',
          type: 'library',
        });
      } else {
        setTreeItem({ id: library.id, open: true, touched: false });

        trackSidebarInteraction({
          action: 'link_click_and_expand',
          type: 'library',
        });
      }
    }, [touched, open, library.id, setTreeItem, trackSidebarInteraction]);

    const libraryPermissions = getLibraryPermissions(library.id);
    const canCreateLibraryBoard = canCreateBoard(libraryPermissions);

    const { onBoardRearrange, getCanRearrange } = useLibraryBoardRearrange({
      library,
      libraryPermissions,
    });

    const handleLoadNextPage = useCallback(() => {
      loadNextPage();
    }, [loadNextPage]);

    const onCreateBoard = useCallback(() => {
      showCreateBoardModal({
        initialLibrary: library,
        trackLocation: 'library',
      });

      trackSidebarInteraction({
        action: 'create_board',
        type: 'library',
      });
    }, [showCreateBoardModal, library, trackSidebarInteraction]);

    const { handleOpenChange: handleLockOpenChange } = useWorkspaceSidebarPushLock({
      token: `sidebar-library-${library.id}`,
    });

    const onDropdownOpenChange = useCallback<Required<DropdownMenuProps>['onOpenChange']>(
      (open) => {
        setIsMenuOpen(open);
        handleLockOpenChange(open);

        if (open) {
          trackSidebarInteraction({
            action: 'open_sub_menu',
            type: 'library',
          });
        }
      },
      [handleLockOpenChange, trackSidebarInteraction],
    );

    const libraryRow = useMemo(
      () => (
        <div
          ref={drop}
          className="group relative flex h-10 ui2026:h-8 shrink-0 cursor-pointer items-center gap-3 ui2026:gap-1.5 ui2026:overflow-hidden rounded-8 px-2 ui2026:py-1 ui2026:pr-1 ui2026:pl-2.5 font-normal ads:text-metadata text-14 text-gray-11 ui2026:text-gray-10 ui2026:leading-[18px] outline-none transition-colors ads:hover:bg-hover-subtle hover:bg-gray-5 ui2026:hover:bg-gray-2 ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset active:bg-gray-5 ads:active:bg-hover-subtle data-[dnd-over=true]:data-[dnd-droppable=true]:z-10 ads:data-[dnd-over=true]:data-[dnd-droppable=true]:bg-subtle data-[dnd-over=true]:data-[dnd-droppable=true]:bg-gray-3 ads:data-[dnd-over=true]:data-[dnd-droppable=true]:ring-primary data-[dnd-over=true]:data-[dnd-droppable=true]:ring-2 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-blue-9 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-inset ads:data-[active=true]:bg-subtle ads:data-[menu-open=true]:bg-subtle data-[active=true]:bg-gray-5 data-[menu-open=true]:bg-gray-5 ui2026:data-[active=true]:bg-gray-3 ui2026:data-[menu-open=true]:bg-gray-3 ui2026:data-[active=true]:font-medium ads:data-[active=true]:text-primary ui2026:data-[active=true]:text-gray-12"
          data-active={isLibraryActive}
          data-dnd-over={isOver}
          data-dnd-droppable={canDrop}
          data-menu-open={isMenuOpen || isContextMenuOpen}
          data-open={open}
          data-testid="WORKSPACE_SIDEBAR_LIBRARIES_LIST_ITEM"
          data-title={library.title}
        >
          <div
            className="relative flex size-6 ui2026:size-5 shrink-0 cursor-pointer items-center justify-center ui2026:overflow-hidden rounded-4 ads:bg-ads-gray-4 bg-gray-4 ads:ring-default ring-1 ring-gray-4 ui2026:ring-inset transition-colors ads:group-hover:ring-transparent group-hover:ring-transparent ui2026:group-hover:ring-gray-2 ads:group-data-[active=true]:ring-subtle ads:group-data-[menu-open=true]:ring-subtle ui2026:group-data-[active=true]:ring-gray-3 ui2026:group-data-[menu-open=true]:ring-gray-3"
            style={{ backgroundColor: library.color?.backgroundHex }}
          >
            <span className="text-14 ui2026:text-12">{library.icon ?? '📚'}</span>

            <button
              aria-expanded={open}
              className="absolute inset-0 z-40 flex size-full items-center justify-center rounded-4 ads:bg-ads-gray-5 bg-gray-5 ads:text-icon-default ui2026:text-gray-11 opacity-0 transition-[background-color,opacity] ads:hover:bg-hover-subtle hover:bg-gray-4 ui2026:hover:bg-gray-3 ads:hover:text-icon-emphasis ui2026:hover:text-gray-12 focus-visible:opacity-100 group-hover:opacity-100"
              data-testid="WORKSPACE_SIDEBAR_LIBRARY_EXPAND_BUTTON"
              onClick={onToggleOpen}
              type="button"
              draggable={false}
            >
              <VisuallyHidden>{open ? 'Collapse' : 'Expand'} library</VisuallyHidden>
              {ui2026 ? (
                <ChevronDownIcon
                  className="text-inherit transition-transform group-data-[open=false]:-rotate-90 group-data-[open=true]:rotate-0"
                  size="xs"
                />
              ) : (
                <ChevronDownIcon
                  className="ads:text-icon-emphasis text-gray-12 transition-transform group-data-[open=false]:-rotate-90 group-data-[open=true]:rotate-0"
                  size="sm"
                />
              )}
            </button>
          </div>

          <Link
            className="absolute inset-0 z-30 size-full rounded-8 outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-blue-9 focus-visible:ring-inset"
            href={{
              pathname: Pathname.LibraryByLibraryId,
              query: {
                ...getCanvasQueryParams(),
                libraryId: library.id,
              },
            }}
            onClick={onLibraryClick}
            draggable={false}
          >
            <VisuallyHidden>Go to {library.title}</VisuallyHidden>
          </Link>

          <span className="grow truncate">{library.title}</span>

          <div className="relative z-40 hidden shrink-0 items-center gap-1 transition-opacity group-focus-within:flex group-hover:flex group-data-[menu-open=true]:flex">
            <DropdownMenu
              align="start"
              onOpenChange={onDropdownOpenChange}
              open={isMenuOpen}
              trigger={
                <TreeItemAction aria-label={`${library.title} actions`} data-testid="LIBRARY_MENU_BUTTON">
                  <EllipsisIcon size="sm" />
                </TreeItemAction>
              }
            >
              <LibraryNaigationDropdownMenuContent library={library} />
            </DropdownMenu>

            {canCreateLibraryBoard && (
              <TreeItemAction
                aria-label={`Create board in ${library.title}`}
                data-testid="ADD_LIBRARY_BOARD_BUTTON"
                onClick={onCreateBoard}
              >
                <PlusIcon size="sm" />
              </TreeItemAction>
            )}
          </div>
        </div>
      ),
      [
        canCreateLibraryBoard,
        canDrop,
        drop,
        isContextMenuOpen,
        isLibraryActive,
        isMenuOpen,
        isOver,
        library,
        onCreateBoard,
        onDropdownOpenChange,
        onLibraryClick,
        onToggleOpen,
        open,
        ui2026,
      ],
    );

    const libraryRowWithContextMenu = useMemo(
      () =>
        ui2026 ? (
          <WorkspaceSidebarLibrariesListItemContextMenu
            lockToken={`${SIDEBAR_LIBRARY_CONTEXT_MENU_LOCK_TOKEN_PREFIX}-${library.id}`}
            onOpenChange={setIsContextMenuOpen}
            options={menuOptions}
            title={library.title}
          >
            {libraryRow}
          </WorkspaceSidebarLibrariesListItemContextMenu>
        ) : (
          libraryRow
        ),
      [library.id, library.title, libraryRow, menuOptions, ui2026],
    );

    return (
      <div className="flex flex-col gap-1 ui2026:gap-0">
        <DNDLibraryDrag
          className="data-[dnd-dragging=true]:opacity-50"
          library={library}
          enabled={draggable}
          index={index}
        >
          {libraryRowWithContextMenu}
        </DNDLibraryDrag>

        {!isLoadingBoards && (libraryRootBoards?.length > 0 || hasMore) ? (
          <WorkspaceSidebarCollapsible open={open}>
            <WorkspaceSidebarBoardsList
              boards={libraryRootBoards}
              depth={ui2026 ? 1 : 2}
              dragType={DragType.boardNavLibrary}
              draggable={true}
              droppable={true}
              getCanRearrange={getCanRearrange}
              onBoardRearrange={onBoardRearrange}
              parentBoardId={null}
            />
            {hasMore ? (
              <Button
                appearance="ghost"
                className="ml-9 justify-start"
                color="gray"
                data-testid="WORKSPACE_SIDEBAR_LIBRARY_BOARDS_SHOW_MORE"
                isLoading={isLoadingMore}
                onClick={handleLoadNextPage}
                size="small"
              >
                Show more
              </Button>
            ) : null}
          </WorkspaceSidebarCollapsible>
        ) : null}
      </div>
    );
  },
);

WorkspaceSidebarLibrariesListItem.displayName = 'WorkspaceSidebarLibrariesListItem';
